import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { ConversationalOnboarding } from './onboarding/ConversationalOnboarding';
import { useLocation } from 'react-router-dom';
import { isTourActiveGlobal, isTourOrTransitioning } from '@/hooks/useTourController';
import { 
  logFlight,
  setAuthHydrated,
  setProfileHydrated,
  getOnboardingState,
  isHydrated,
  type OnboardingStage
} from '@/hooks/useOnboardingStateMachine';
import { diagLog } from '@/utils/diagnosticSession';

/**
 * OnboardingWrapper - Deterministic first-run gating
 * 
 * Uses the OnboardingStateMachine as SINGLE SOURCE OF TRUTH.
 * Only renders ConversationalOnboarding when state === 'onboarding'.
 * 
 * DOES NOT perform any redirects or routing decisions.
 * Only renders appropriate content based on state machine state.
 */
export default function OnboardingWrapper({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [state, setState] = useState<OnboardingStage>('hydrating');
  const location = useLocation();

  // Hydrate auth and profile on mount
  useEffect(() => {
    logFlight('WRAPPER_INIT', { path: location.pathname });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      const userId = session?.user?.id ?? null;
      setUser(session?.user ?? null);
      setAuthHydrated(userId);
      
      logFlight('WRAPPER_AUTH_RESOLVED', { 
        path: location.pathname,
        hasUser: !!session?.user,
        userId
      });
      
      // If we have a user, fetch their profile
      if (session?.user) {
        supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('user_id', session.user.id)
          .maybeSingle() // Use maybeSingle to avoid 406 errors when no row exists
          .then(({ data, error }) => {
            // CRITICAL: On error or no data, default to FALSE (needs onboarding)
            const completed = error ? false : (data?.onboarding_completed ?? false);
            setProfileHydrated(completed);
            setState(getOnboardingState());
            
            logFlight('WRAPPER_PROFILE_RESOLVED', { 
              path: location.pathname,
              onboardingCompleted: completed,
              error: error?.message,
              newState: getOnboardingState()
            });
          });
      } else {
        // No user - still hydrated but at auth state
        setProfileHydrated(null);
        setState(getOnboardingState());
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      logFlight('WRAPPER_AUTH_CHANGE', { event, hasUser: !!session?.user });
      
      // CRITICAL: Skip auth changes if tour is active OR transitioning from onboarding
      if (isTourOrTransitioning()) {
        logFlight('WRAPPER_AUTH_CHANGE_IGNORED', { reason: 'tour_or_transitioning' });
        return;
      }
      
      setUser(session?.user ?? null);
      setAuthHydrated(session?.user?.id ?? null);
      
      if (session?.user) {
        supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('user_id', session.user.id)
          .maybeSingle()
          .then(({ data, error }) => {
            const completed = error ? false : (data?.onboarding_completed ?? false);
            setProfileHydrated(completed);
            setState(getOnboardingState());
          });
      } else {
        setProfileHydrated(null);
        setState(getOnboardingState());
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Update state on location change (for debugging)
  useEffect(() => {
    const currentState = getOnboardingState();
    logFlight('WRAPPER_ROUTE_CHANGE', { 
      path: location.pathname, 
      state: currentState,
      hydrated: isHydrated()
    });
    setState(currentState);
  }, [location.pathname]);

  // Public routes that bypass onboarding
  const isPublicRoute = [
    '/auth', '/reset-password', '/terms', '/privacy', '/landing', 
    '/beta', '/admin', '/admin-portal', '/whoop', '/whoop-login', 
    '/whoop-dashboard', '/wellness-tracks', '/onboarding', '/tour-preview'
  ].includes(location.pathname);
  
  const isWhoopDemoUser = user?.email?.toLowerCase() === 'support@therappi.app';

  // Handler for when conversational onboarding completes
  const handleOnboardingComplete = useCallback(() => {
    logFlight('WRAPPER_ONBOARDING_COMPLETE', {});
    // Force state refresh - the state machine should now be in 'welcome' state
    setState(getOnboardingState());
  }, []);

  // DIAGNOSTIC: Log render
  diagLog('OnboardingWrapper', 'RENDER', {
    path: location.pathname,
    state,
    hydrated: isHydrated(),
    hasUser: !!user,
    isPublicRoute,
    tourActive: isTourActiveGlobal()
  });

  // ========== RULE 0: MASTER HYDRATION GATE ==========
  // NOTHING renders until fully hydrated (except public routes)
  if (state === 'hydrating' && !isPublicRoute) {
    logFlight('WRAPPER_BLOCKED_HYDRATING', { path: location.pathname });
    return (
      <div className="fixed inset-0 min-h-screen flex items-center justify-center bg-[hsl(182,29%,26%)] z-50">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-4 border-[hsl(177,41%,76%)] border-t-[hsl(182,37%,46%)] rounded-full animate-spin" />
          <p className="text-[hsl(177,41%,76%)] font-thin tracking-wide">Loading...</p>
        </div>
      </div>
    );
  }

  // ========== RULE 1: TOUR STATE BYPASS ==========
  // If in tour state, render children immediately (tour overlay handles itself)
  if (state === 'tour' && !isPublicRoute) {
    logFlight('WRAPPER_TOUR_BYPASS', { path: location.pathname });
    return <>{children}</>;
  }

  // ========== RULE 2: CONVERSATIONAL STATE ==========
  // Show onboarding when state is 'conversational' and not on public routes
  if (state === 'conversational' && user && !isPublicRoute && !isWhoopDemoUser) {
    logFlight('WRAPPER_SHOWING_ONBOARDING', { path: location.pathname });
    return <ConversationalOnboarding onComplete={handleOnboardingComplete} />;
  }

  // All other states (auth, welcome, app) render children
  // - HomeRedirect in App.tsx handles auth routing
  // - TherappiWelcomeHandler handles the 'welcome' stage display
  // - TourContainer handles the 'tour' stage
  return <>{children}</>;
}
